import { Trans, useTranslation } from 'react-i18next'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Dialog, Box, Text, Stack, Button, Inline, List } from '@machobear/design-system'
import { FieldInput } from '@/components/ds-form'

type Props = {
  visible: boolean
  closeModal: () => void
  handleDelete: () => void
}

const DeleteConfirmationModal = ({ visible, closeModal, handleDelete }: Props) => {
  const { t } = useTranslation()

  const onSubmit = () => {
    closeModal()
    handleDelete()
  }

  const validationSchema = Yup.object().shape({
    confirmation: Yup.string()
      .required(t('Required: Please enter DELETE to continue'))
      .matches(/^DELETE$/, t('Please enter DELETE to continue'))
  })

  return (
    <Dialog
      width="small"
      id="delete-confirm-dialog"
      open={visible}
      onClose={closeModal}
      title={t('Delete listing?', 'Delete listing?')}
    >
      <Formik
        initialValues={{ confirmation: '' }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <Form>
          <Stack space="large">
            <Stack space="gutter">
              <Trans i18nKey="l.listing.edit.delete_confirm.text">
                <Stack space="small">
                  <Text>By deleting this listing, I understand that:</Text>
                  <List space="small">
                    <Text>This listing will be deleted from my account</Text>
                    <Text>This listing will be unpublished and hidden from renters</Text>
                    <Text>
                      All saved data, including chat rooms and applications, will be deleted
                    </Text>
                  </List>
                </Stack>
              </Trans>
              <Text weight="medium">
                {t(
                  'l.listing.edit.delete_confirm.label',
                  'Please confirm by typing “DELETE” below:'
                )}
              </Text>
              <FieldInput
                name="confirmation"
                label={t('l.listing.edit.delete_confirm.placeholder', 'Enter DELETE to continue')}
                required
              />
            </Stack>
            <Box display="flex" justifyContent="flexEnd">
              <Inline space="small">
                <Button variant="transparent" onClick={closeModal}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button type="submit" tone="critical">
                  {t('Delete listing', 'Delete listing')}
                </Button>
              </Inline>
            </Box>
          </Stack>
        </Form>
      </Formik>
    </Dialog>
  )
}

export default DeleteConfirmationModal
